import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import "@h2j-data-team/tokens/fonts.css";
import "@h2j-data-team/tokens/tokens.css";
import "@h2j-data-team/ui/styles.css";
import "./index.css";
import "./i18n";
import { ThemeProvider } from "./lib/theme";
import { AppErrorBoundary, NotFoundScreen, RouteErrorComponent } from "./components/error-screen";
import { applyDensityToDocument } from "./lib/use-table-density";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({ autoInject: false }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Paint the persisted table density before first render so tables never flash
// at the wrong scale.
applyDensityToDocument();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteErrorComponent,
  defaultNotFoundComponent: NotFoundScreen,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppErrorBoundary>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
